import { useState, useEffect, useRef, useCallback } from 'react';
import { getCookie } from '../components/utils/utils';
import { useTranslation } from 'react-i18next';

interface AddressData {
  street?: string;
  houseNumber?: string;
  city?: string;
  postcode?: string;
  country?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface PropertyData {
  address?: AddressData;
  description?: string;
  size?: string | number;
  rooms?: string;
  floor?: string | number;
  hasElevator?: boolean;
  hasBalcony?: boolean;
  renovationStatus?: string;
  buildingType?: string;
  building_id?: string;
  willingness_id?: string;
}

interface EstimationResult {
  report_text: string;
  estimated_price: number | null;
  price_range: {
    min: number | null;
    max: number | null;
  };
  confidence_level: string;
  price_per_sqm: number | null;
  market_factors: string[];
  comparable_properties: string[];
  recommendations: string[];
  sources: string[];
}

interface EstimationData {
  estimation_id: string;
  address: AddressData;
  property_details: any;
  result: EstimationResult;
  created_at: string;
  completed_at: string | null;
}

export type EstimationStatus = 'idle' | 'queued' | 'in_progress' | 'completed' | 'failed' | 'error';

interface UsePriceEstimationReturn {
  estimation: EstimationData | null;
  status: EstimationStatus;
  error: string | null;
  startEstimation: () => Promise<void>;
  estimationId: string | null;
  isPolling: boolean;
  resetEstimation: () => void;
}

const POLLING_INTERVAL = 10000; // Poll every 10 seconds (webhooks handle the heavy lifting)
const MAX_POLLING_DURATION = 60 * 60 * 1000; // Stop polling after 60 minutes

export const usePriceEstimation = (
  propertyData: PropertyData,
  isDetailsExpanded: boolean,
  isLoggedIn: boolean,
  buildingAddress?: AddressData,
  checkActiveEstimation: boolean = false
): UsePriceEstimationReturn => {
  const [estimation, setEstimation] = useState<EstimationData | null>(null);
  const [status, setStatus] = useState<EstimationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [estimationId, setEstimationId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const hasTriggeredRef = useRef(false);
  const isMountedRef = useRef(true);
  const hasCheckedExistingRef = useRef(false);
  const { i18n } = useTranslation();
  const language = i18n.language;

  // Check if we have minimum data for estimation
  const hasMinimumData = useCallback((data: PropertyData): boolean => {
    // We need at least an address (city or street) and country
    const address = data.address || buildingAddress;
    if (!address) {
      return false;
    }

    const hasLocation = !!(address.city || address.street);
    const hasCountry = !!address.country;

    return hasLocation && hasCountry;
  }, [buildingAddress]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsPolling(false);
    pollingStartTimeRef.current = null;
  }, []);

  // Fetch the persisted full report for an estimation. This includes the
  // address and property details stored when the estimation was created,
  // which may differ from the current (possibly empty) local form data.
  const fetchFullReport = useCallback(async (id: string): Promise<EstimationData | null> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/price-estimation/report/${id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          }
        }
      );

      if (!response.ok) {
        console.error(`Failed to fetch full report for estimation ${id}`);
        return null;
      }

      return await response.json();
    } catch (err) {
      console.error('Error fetching full estimation report:', err);
      return null;
    }
  }, []);

  // Check estimation status
  const checkStatus = useCallback(async (id: string): Promise<void> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/price-estimation/status/${id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check estimation status');
      }

      const data = await response.json();

      if (!isMountedRef.current) return;

      if (data.status === 'completed' && data.data) {
        // Estimation completed successfully.
        // Hydrate from the persisted report so property details match what
        // was saved when the estimation was created, instead of rebuilding
        // them from the local form data (which may be empty).
        const fullReport = await fetchFullReport(id);

        if (!isMountedRef.current) return;

        if (fullReport) {
          setEstimation(fullReport);
        } else {
          // Fallback: build from local data if the report fetch fails
          setEstimation({
            estimation_id: id,
            address: propertyData.address || buildingAddress || {},
            property_details: {
              description: propertyData.description,
              size: propertyData.size,
              rooms: propertyData.rooms,
              floor: propertyData.floor,
              hasElevator: propertyData.hasElevator,
              hasBalcony: propertyData.hasBalcony,
              renovationStatus: propertyData.renovationStatus,
              buildingType: propertyData.buildingType
            },
            result: data.data,
            created_at: new Date().toISOString(),
            completed_at: data.completed_at || new Date().toISOString()
          });
        }
        setStatus('completed');
        stopPolling();

      } else if (data.status === 'failed' || data.status === 'incomplete') {
        // Estimation failed or stopped incomplete (both are terminal states)
        setError(data.error || 'Estimation failed or stopped');
        setStatus('failed');
        stopPolling();

      } else {
        // Still in progress
        setStatus(data.status as EstimationStatus);
      }

    } catch (err) {
      console.error('Error checking estimation status:', err);
      // Don't stop polling on error - might be temporary network issue
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to check status');
      }
    }
  }, [stopPolling, fetchFullReport]);

  // Start polling for status updates
  const startPolling = useCallback((id: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    setIsPolling(true);
    pollingStartTimeRef.current = Date.now();

    pollingRef.current = setInterval(async () => {
      // Check if we've exceeded maximum polling duration
      if (pollingStartTimeRef.current &&
        Date.now() - pollingStartTimeRef.current > MAX_POLLING_DURATION) {
        console.log('Exceeded maximum polling duration, stopping...');
        stopPolling();
        if (isMountedRef.current) {
          setError('Estimation is taking longer than expected.');
          setStatus('error');
        }
        return;
      }

      await checkStatus(id);
    }, POLLING_INTERVAL);

    // Also check immediately
    checkStatus(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkStatus, stopPolling]);

  // Start a new estimation
  const startEstimation = useCallback(async () => {
    if (!isLoggedIn) {
      setError('Please log in to use price estimation');
      setStatus('error');
      return;
    }

    if (!hasMinimumData(propertyData)) {
      setError('Insufficient property data for estimation');
      setStatus('error');
      return;
    }

    try {
      setStatus('queued');
      setError(null);

      // Prepare the request data
      const address = propertyData.address || buildingAddress || {};
      const requestData = {
        address: {
          street: address.street,
          houseNumber: address.houseNumber,
          city: address.city,
          postcode: address.postcode,
          country: address.country || 'FI', // Default to Finland
          coordinates: address.coordinates
        },
        property_details: {
          description: propertyData.description || '',
          size: propertyData.size ? Number(propertyData.size) : null,
          rooms: propertyData.rooms || '',
          floor: propertyData.floor ? Number(propertyData.floor) : null,
          hasElevator: propertyData.hasElevator || false,
          hasBalcony: propertyData.hasBalcony || false,
          renovationStatus: propertyData.renovationStatus || '',
          buildingType: propertyData.buildingType || ''
        },
        building_id: propertyData.building_id,
        willingness_id: propertyData.willingness_id
      };

      console.log('Starting price estimation with data:', requestData);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-estimation/start`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify({ ...requestData, language, user_agent: navigator.userAgent })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start estimation');
      }

      const data = await response.json();

      if (isMountedRef.current) {
        setEstimationId(data.estimation_id);
        setStatus(data.status === 'completed' ? 'completed' : 'in_progress');

        // If not immediately completed, start polling
        if (data.status !== 'completed') {
          startPolling(data.estimation_id);
        } else {
          // If immediately completed, fetch the full result
          await checkStatus(data.estimation_id);
        }
      }

    } catch (err) {
      console.error('Error starting price estimation:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to start price estimation');
        setStatus('error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, propertyData, buildingAddress, hasMinimumData, checkStatus, startPolling]);

  // Reset estimation state
  const resetEstimation = useCallback(() => {
    setEstimation(null);
    setStatus('idle');
    setError(null);
    setEstimationId(null);
    hasTriggeredRef.current = false;
    hasCheckedExistingRef.current = false;
    stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the latest estimation for the current address
  const fetchActiveEstimationForBuilding = useCallback(async () => {
    if (!isLoggedIn || !propertyData.building_id) {
      return null;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/price-estimation/history?building_id=${propertyData.building_id}&limit=5`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Check if any recent estimation is in progress
        const activeEstimation = data.estimations.find((est: any) => 
          est.status === 'queued' || est.status === 'in_progress' || est.status === 'incomplete'
        );

        if (activeEstimation && isMountedRef.current) {
          console.log('Found active estimation for building:', activeEstimation);
          setEstimationId(activeEstimation._id);
          setStatus(activeEstimation.status as EstimationStatus);
          
          // Start polling for this active estimation
          startPolling(activeEstimation._id);
          return activeEstimation;
        }
      }
    } catch (err) {
      console.error('Error fetching active estimation for building:', err);
    }
    return null;
  }, [isLoggedIn, propertyData.building_id, startPolling]);

  // Fetch the latest estimation for the current address
  const fetchLatestEstimation = useCallback(async () => {
    if (!isLoggedIn || !hasMinimumData(propertyData)) {
      return;
    }

    const address = propertyData.address || buildingAddress || {};

    // Don't fetch if we don't have meaningful address data
    if (!address.street && !address.city) {
      return;
    }

    try {
      const requestData = {
        address: {
          street: address.street,
          houseNumber: address.houseNumber,
          city: address.city,
          postcode: address.postcode
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-estimation/latest-by-address`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const data = await response.json();

        if (isMountedRef.current && data.status === 'completed' && data.data) {
          // Hydrate from the persisted report so property details match what
          // was saved when the estimation was created, instead of rebuilding
          // them from the local form data (which may be empty).
          const fullReport = await fetchFullReport(data.estimation_id);

          if (!isMountedRef.current) return;

          if (fullReport) {
            setEstimation(fullReport);
          } else {
            // Fallback: build from local data if the report fetch fails
            setEstimation({
              estimation_id: data.estimation_id,
              address: data.address || address,
              property_details: {
                description: propertyData.description,
                size: propertyData.size,
                rooms: propertyData.rooms,
                floor: propertyData.floor,
                hasElevator: propertyData.hasElevator,
                hasBalcony: propertyData.hasBalcony,
                renovationStatus: propertyData.renovationStatus,
                buildingType: propertyData.buildingType
              },
              result: data.data,
              created_at: new Date().toISOString(),
              completed_at: data.completed_at || new Date().toISOString()
            });
          }
          setEstimationId(data.estimation_id);
          setStatus('completed');
          console.log('Found existing estimation:', data.estimation_id);
        }
      } 
    } catch (err) {
      console.log('Error fetching latest estimation:', err);
      // Don't set error state - this is just checking for existing estimations
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, propertyData, buildingAddress, hasMinimumData, fetchFullReport]);

  // Check for existing estimation when modal opens or address changes
  useEffect(() => {
    if (isLoggedIn && hasMinimumData(propertyData) && !hasCheckedExistingRef.current) {
      hasCheckedExistingRef.current = true;
      
      const checkEstimations = async () => {
        let activeEstimation = null;
        
        // 1. First check if there is an active (in-progress) estimation for this building
        // Only if checkActiveEstimation is true (e.g. in BuildingModalGlobal)
        if (checkActiveEstimation) {
          activeEstimation = await fetchActiveEstimationForBuilding();
        }
        
        // 2. If no active estimation, check for latest completed estimation
        if (!activeEstimation) {
          fetchLatestEstimation();
        }
      };
      
      checkEstimations();
    }
  }, [isLoggedIn, propertyData, hasMinimumData, fetchLatestEstimation, fetchActiveEstimationForBuilding, checkActiveEstimation]);

  // Reset the checked flag when modal closes (component unmounts)
  useEffect(() => {
    return () => {
      hasCheckedExistingRef.current = false;
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  return {
    estimation,
    status,
    error,
    startEstimation,
    estimationId,
    isPolling,
    resetEstimation
  };
};
